import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TGetTripRes, TransportRes, TTripForCargoRes, UserRes } from 'api/swagger';
import { useTransformToTableData } from 'shared/hooks/use-transform-to-table-data';
import { formatWithPostfixOrEmpty } from 'shared/utils/format-with-postfix-or-empty';
import { formatDate } from 'shared/utils/time/format-date.ts';
import { TransportSubTypes } from 'types/transport';
import { ImportantTripDocumentFlags } from 'components/documents/important-trip-document-flags';
import { TripRoute } from 'components/trips/trip-route';
import { TripStatusTag } from 'components/trips/trip-status-tag';
import { TripsSummary } from 'components/trips/trips-summary';
import { VehicleLoad } from '../vehicle-load';
import styles from './trips-list.module.scss';
import type { TTripListProps } from './types';

export const TripsList: React.FC<TTripListProps> = (props) => {
  const { trips, isLoading, withoutValues = false, withoutSummary = false, isForSpecificCargo } = props;

  const { t } = useTranslation();
  const tableData = useTransformToTableData(trips);

  const columns: ColumnsType<TGetTripRes | TTripForCargoRes> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: '8%',
      render: (id: string, { compositeId, documents }) => (
        <>
          <Link to={`/trips/${id}`}>{compositeId}</Link>
          {documents ? (
            <div className={styles.documentTags}>
              <ImportantTripDocumentFlags documents={documents} />
            </div>
          ) : null}
        </>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: '8%',
      render: (status) => (
        <>
          <TripStatusTag status={status} />
        </>
      ),
    },
    {
      title: t('trip:direction'),
      dataIndex: 'id',
      width: '15%',
      render: (_, { estimatedStartDate, estimatedEndDate, relatedWarehouseIds }) => {
        return (
          <>
            <TripRoute warehousesIds={relatedWarehouseIds} />
            <div className={styles.secondary}>
              {formatDate(estimatedStartDate)} {'->'} {formatDate(estimatedEndDate)}
            </div>
          </>
        );
      },
    },
    ...(isForSpecificCargo
      ? [
          {
            title: t('trip:cost_price'),
            dataIndex: 'costPrice',
            width: '8%',
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            render: (_: any, { cargoCostPrice, cargoCostPriceByMap }: TTripForCargoRes) => {
              if (!cargoCostPrice && !cargoCostPriceByMap) {
                return null;
              }

              return (
                <>
                  <div>{formatWithPostfixOrEmpty(cargoCostPrice, '€')}</div>
                  <div className={styles.secondary}>{formatWithPostfixOrEmpty(cargoCostPriceByMap, '€')}</div>
                </>
              );
            },
          },
        ]
      : []),
    {
      title: t('trip:total_cost_price'),
      dataIndex: 'totalCostPrice',
      width: '8%',
      render: (_, { costPriceInDefault, calculatedCostPrice, costPriceByMap }) => {
        const price = costPriceInDefault || calculatedCostPrice;

        if (!price && !costPriceByMap) {
          return null;
        }

        return (
          <>
            <div>{formatWithPostfixOrEmpty(price, '€')}</div>
            <div className={styles.secondary}>{formatWithPostfixOrEmpty(costPriceByMap, '€')}</div>
          </>
        );
      },
    },

    {
      title: t('trip:truck'),
      dataIndex: 'truck',
      width: '15%',
      render: (truck: TransportRes, { trailer, trailerPlate, soldToPartners, carrier }) => {
        return soldToPartners && carrier ? (
          <>
            Sold:{' '}
            <Link className={styles.carrier} to={`/clients/${carrier.id}`}>
              {carrier.shortName || carrier.name}
            </Link>
          </>
        ) : (
          <>
            <div>
              {truck.plateNumber} <span className={styles.secondary}>{TransportSubTypes[truck.subType]}</span>
            </div>
            <div>
              {trailer ? (
                <Typography.Text>
                  {trailer.plateNumber} <span className={styles.secondary}>{TransportSubTypes[trailer.subType]}</span>
                </Typography.Text>
              ) : null}
              {trailerPlate ? <Typography.Text className={styles.gray}>{trailerPlate}</Typography.Text> : null}
            </div>
          </>
        );
      },
    },
    ...(!withoutValues
      ? [
          {
            title: t('trip:total_values'),
            dataIndex: 'values',
            render: (_: unknown, { totalLDM, totalVolume, totalWeight }: TGetTripRes) => {
              return (
                <>
                  <Typography.Text>
                    LDM: {totalLDM} / Volume: {totalVolume} / Weight: {totalWeight}
                  </Typography.Text>
                </>
              );
            },
          },
        ]
      : []),
    ...(!withoutValues
      ? [
          {
            title: t('trip:vehicle_load'),
            dataIndex: 'values',
            render: (_: unknown, trip: TGetTripRes) => {
              return <VehicleLoad loadByWeight={trip.loadByWeight} loadByLDM={trip.loadByLDM} status={trip.status} />;
            },
          },
        ]
      : []),
    ...(!withoutValues
      ? [
          {
            title: t('trip:freight_sum'),
            dataIndex: 'values',
            render: (_: unknown, { totalFreight }: TGetTripRes) => {
              return <Typography.Text>{totalFreight ? `${totalFreight} €` : ''}</Typography.Text>;
            },
          },
        ]
      : []),
    {
      title: 'Manager',
      dataIndex: 'user',
      width: '15%',
      render: (user: UserRes, { createdAt }) => {
        return (
          <>
            <div>{user.name}</div>
            <div className={styles.secondary}>{formatDate(createdAt)}</div>
          </>
        );
      },
    },
  ];

  return (
    <div>
      {!withoutSummary && <TripsSummary />}
      <Table
        size={'small'}
        columns={columns}
        dataSource={tableData}
        pagination={false}
        loading={isLoading ? { size: 'large', delay: 200 } : false}
        bordered
      />
    </div>
  );
};
